import React, { useState, useEffect, useMemo } from "react";
import {
  Star,
  Heart,
  ShoppingCart,
  Truck,
  Award,
  User,
  WandSparkles,
  TrendingUp,
  HandCoins,
  StarHalf,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  PackageSearch,
  Mails,
  CheckCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import parse from "html-react-parser";
import he from "he";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import {
  fetchProductById as fetchProductDetailById,
  setProduct,
  clearProduct,
  selectProductDetails,
  selectProductDetailsLoading,
  selectProductDetailsError,
} from "store/slices/productDetailsSlice";
import { selectProductById as selectPLPProductById } from "store/slices/productsSlice";
import {
  addToCart,
  selectCartItems,
  selectCartStatus,
} from "store/slices/cartSlice";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet-async";
import NotifyAddTocart from "shared/NotifyAddTocart";
import ModalViewAllReviews from "../../shared/Reviews/ModalViewAllReviews";
import BulkEnquiryDialogue from "shared/BulkEnquiryDialogue/BulkEnquiryDialogue";
import { AnimatePresence, motion } from "framer-motion";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import NcInputNumber from "shared/NcInputNumber";
import { filterImageByAttributes } from "utils/AttributeHandlers";
import { GlowingBadge } from "shared/Badge/GlowingBadge";
import SimilarProducts from "@/components/products/SimilarProducts";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import { getUserId } from "services/cookieService";
import { openEnquiryDialogue } from "store/slices/enquiryFormSlice";
import { IProduct, IVariant } from "Interfaces/interface.productsapi";
import { ITEM_TYPES } from "utils/itemTypeHelpers";
import { handleMockUpNavigation } from "utils/authHandlers";
import Tippy from "@tippyjs/react";
import { openModal } from "store/slices/authModalSlice";
import { selectIsAuthenticated } from "store/slices/cognitoAuthSlice";
import { restrictGenerationLimit } from "utils/toolsUtilities";
import { selectUserMockups } from "store/slices/mockupSlice";
import { PlatformServices } from "data/tools.enum";
import EmptyState from "components/EmptyState/EmptyState";
import ErrorFallback from "shared/ErrorFallback/ErrorFallback";
import ToTitleCase from "utils/ToTitleCase";
import { selectUser } from "store/slices/authSlice";
import {
  selectPDPConfigurationByProductId,
  selectPDPConfigurationExists,
  fetchPDPConfigurationAsync,
  selectPDPConfigurationLoading,
  selectCartConfigurationLoading,
} from "store/slices/configurationSlice";
import GoBackBtn from "shared/GoBackBtn/GoBackBtn";
import { Alert, AlertDescription } from "@/components/UI/alert";

const ImageGallery: React.FC<{
  images: string[];
  product: IProduct;
  attributesSelected: any;
  itemKey: string;
  productSelected: IVariant;
}> = ({ images, product, attributesSelected, itemKey, productSelected }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const navigate = useNavigate();
  const generatedMockups = useAppSelector(selectUserMockups);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const isInternalUser = user?.emailAddress?.endsWith("@360customizer.com");
  const dispatch = useAppDispatch();
  const cartState = useAppSelector(selectCartStatus);
  const pdpConfiguration = useAppSelector(
    selectPDPConfigurationByProductId(product?.identification?.NPIN || "")
  );

  const hasPDPConfiguration = useAppSelector(
    selectPDPConfigurationExists(product?.identification?.NPIN || "")
  );

  const isPDPConfigurationLoading = useAppSelector(
    selectPDPConfigurationLoading
  );

  useEffect(() => {
    setSelectedImage(0);
  }, [images]);

  const handleGenerateMockup = () => {
    if (
      !restrictGenerationLimit(
        PlatformServices.MOCKUP,
        generatedMockups.length,
        isInternalUser
      )
    ) {
      return;
    }

    const finalUrl = handleMockUpNavigation(product, attributesSelected);

    if (finalUrl) {
      navigate(finalUrl);
    }
  };

  useEffect(() => {
    if (!product?.identification?.NPIN) return;

    if (!hasPDPConfiguration && isAuthenticated) {
      dispatch(fetchPDPConfigurationAsync(product.identification.NPIN));
    }
  }, [
    product?.identification?.NPIN,
    dispatch,
    hasPDPConfiguration,
    isAuthenticated,
  ]);

  const isMockupAvailable = useMemo(() => {
    if (!pdpConfiguration || !attributesSelected) return false;

    const finalUrl = handleMockUpNavigation(product, attributesSelected);
    if (!finalUrl) return false;

    const configurationId = finalUrl.split("/").pop();
    if (!configurationId) return false;

    return pdpConfiguration.configuredCombinations.some(
      (combo) => combo.toLowerCase() === configurationId.toLowerCase()
    );
  }, [pdpConfiguration, attributesSelected, product]);

  return (
    <div className="sticky top-28 flex flex-col lg:flex-row gap-4">
      <div
        className={` flex gap-2 order-2 lg:order-1 md:mt-10 lg:mt-0 lg:justify-start overflow-auto
    lg:overflow-x-hidden
    lg:flex-col
    lg:max-h-[440px] 
    max-w-full    
    thin-scrollbar   
    scrollbar-stable
  `}
      >
        {images?.map((image, index) => (
          <button
            key={index}
            onClick={() => setSelectedImage(index)}
            className={`w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${selectedImage === index
              ? "border-black"
              : "border-gray-200 hover:border-gray-300"
              }`}
          >
            <img
              src={image}
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>

      <div className="w-full max-w-[300px] md:max-w-[300px] lg:max-w-[400px] order-1 lg:order-2 flex flex-col mx-auto">
        <div className="relative w-full aspect-[3/4] max-h-[320px] lg:max-h-[470px] rounded-lg overflow-visible lg:overflow-hidden bg-white">
          <Tippy
            content={
              <span className="text-sm">
                {!isAuthenticated ? (
                  <>
                    Please sign in to generate a mockup.
                    <button
                      onClick={() => dispatch(openModal())}
                      className="text-indigo-200 underline hover:text-indigo-800 transition"
                    >
                      Sign In
                    </button>
                  </>
                ) : (
                  !isMockupAvailable &&
                  "This variant is not available for mockup generation. Please select another."
                )}
              </span>
            }
            trigger="mouseenter click focus"
            interactive={true}
            theme="gradient"
            placement="bottom"
            disabled={isMockupAvailable}
          >
            <div className="absolute right-2 z-20">
              <ButtonPrimary
                disabled={
                  !isAuthenticated ||
                  cartState === "loading" ||
                  isPDPConfigurationLoading ||
                  !isMockupAvailable
                }
                loading={isPDPConfigurationLoading}
                className="disabled:bg-white disabled:border-2 disabled:border-gray-400/40 "
                onClick={handleGenerateMockup}
              >
                <WandSparkles className="ml-1 w-4 h-4" /> Mockup
              </ButtonPrimary>
            </div>
          </Tippy>

          <button
            onClick={() => setSelectedImage((prev) => Math.max(prev - 1, 0))}
            disabled={selectedImage === 0}
            className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white rounded-full shadow-xl ${selectedImage === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
            aria-label="Previous image"
          >
            <ChevronLeft className="text-black font-bold" />
          </button>

          <img
            src={images[selectedImage]}
            alt="Main product view"
            className="w-full h-full object-contain"
          />

          <button
            onClick={() =>
              setSelectedImage((prev) => Math.min(prev + 1, images.length - 1))
            }
            disabled={selectedImage === images.length - 1}
            className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white rounded-full shadow-xl ${selectedImage === images.length - 1
              ? "opacity-50 cursor-not-allowed"
              : ""
              }`}
            aria-label="Next image"
          >
            <ChevronRight className="text-black font-bold" />
          </button>
        </div>
      </div>
    </div>
  );
};

const Rating: React.FC<{ rating: number; size?: "sm" | "md" | "lg" }> = ({
  rating,
  size = "md",
}) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, index) => {
        if (index < fullStars) {
          return (
            <Star
              key={index}
              className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`}
            />
          );
        } else if (index === fullStars && hasHalfStar) {
          return (
            <StarHalf
              key={index}
              className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`}
            />
          );
        } else {
          return (
            <Star
              key={index}
              className={`${sizeClasses[size]} fill-gray-300 text-gray-400`}
            />
          );
        }
      })}
    </div>
  );
};

const ProductInfo: React.FC<{
  product: any;
  images: any;
  setVariantSelected: any;
  setProductSelected: any;
  normalize: (val: any) => string;
}> = ({
  product,
  images,
  setVariantSelected,
  setProductSelected,
  normalize,
}) => {
    const dispatch = useAppDispatch();
    const [quantity, setQuantity] = useState(25);
    const [isDialogueOpen, setIsDialogueOpen] = useState(false);
    const [addingToCart, setAddingToCart] = useState(false);
    const [isQuantityChanging, setIsQuantityChanging] = useState(false);
    const [isQuantityInputFocused, setIsQuantityInputFocused] = useState(false);
    const userId = getUserId();
    const normalizeAttributeName = (name: string) => name.trim().toLowerCase();
    const normalizeAttributeValue = (value: string) => value.trim().toLowerCase();
    const cartItems = useAppSelector(selectCartItems);
    const configLoading = useAppSelector(selectCartConfigurationLoading);
    const [selectedAttributes, setSelectedAttributes] = useState(() => {
      const initial: Record<string, string> = {};

      let currentNode = product.availableAttributes?.root;

      while (currentNode && !currentNode.isLeaf && currentNode.children) {
        const attributeName = normalizeAttributeName(currentNode.attributeName);
        const firstOptionKey = Object.keys(currentNode.children)[0];

        if (attributeName && firstOptionKey !== undefined) {
          const firstOption = currentNode.children[firstOptionKey];
          initial[attributeName] = normalizeAttributeValue(firstOption.value);
          currentNode = firstOption;
        } else {
          break;
        }
      }

      return initial;
    });

    const selectedVariant = useMemo(() => {
      return product.variants?.find((variant: any) => {
        if (!variant.attributes) return false;

        return Object.entries(selectedAttributes).every(([key, value]) => {
          const matchingKey = Object.keys(variant.attributes).find(
            (k) => normalizeAttributeName(k) === normalizeAttributeName(key)
          );

          if (!matchingKey) return false;

          return (
            normalizeAttributeValue(variant.attributes[matchingKey]) ===
            normalizeAttributeValue(value)
          );
        });
      });
    }, [product.variants, selectedAttributes]);

    const handleAttributeChange = (attributeName: string, value: string) => {
      setSelectedAttributes((prev) => {
        const normalizedAttributeName = normalizeAttributeName(attributeName);
        const normalizedValue = normalizeAttributeValue(value);
        const newAttributes = {
          ...prev,
          [normalizedAttributeName]: normalizedValue,
        };

        const treePath = getCurrentTreePath();

        const changedAttributeIndex = treePath.findIndex(
          (level: any) =>
            normalizeAttributeName(level.attributeName) ===
            normalizedAttributeName
        );

        if (changedAttributeIndex !== -1) {
          newAttributes[normalizedAttributeName] = normalizedValue;

          let currentNode = product.availableAttributes?.root;
          for (let i = 0; i <= changedAttributeIndex; i++) {
            const level = treePath[i];
            const selectedValue =
              newAttributes[normalizeAttributeName(level.attributeName)];

            if (currentNode.children) {
              const matchingChildKey = Object.keys(currentNode.children).find(
                (childKey) =>
                  normalizeAttributeValue(
                    currentNode.children[childKey].value
                  ) === selectedValue
              );

              if (matchingChildKey && currentNode.children[matchingChildKey]) {
                currentNode = currentNode.children[matchingChildKey];
              } else {
                break;
              }
            } else {
              break;
            }
          }

          while (currentNode && !currentNode.isLeaf && currentNode.children) {
            const nextAttributeName = normalizeAttributeName(
              currentNode.attributeName
            );
            const firstOptionKey = Object.keys(currentNode.children)[0];
            if (nextAttributeName && firstOptionKey !== undefined) {
              newAttributes[nextAttributeName] = normalizeAttributeValue(
                currentNode.children[firstOptionKey].value
              );
              currentNode = currentNode.children[firstOptionKey];
            } else {
              break;
            }
          }

          Object.keys(newAttributes).forEach((key) => {
            const normalizedKey = normalizeAttributeName(key);
            if (key !== normalizedKey && newAttributes[normalizedKey]) {
              delete newAttributes[key];
            }
          });
        }

        return newAttributes;
      });
    };

    useEffect(() => {
      if (selectedVariant) {
        setProductSelected(selectedVariant);
        setVariantSelected(selectedAttributes);
      } else {
        setProductSelected(product.variants?.[0] || null);
        setVariantSelected(selectedAttributes);
      }
    }, [
      product,
      selectedVariant,
      selectedAttributes,
      setProductSelected,
      setVariantSelected,
    ]);

    const getCurrentTreePath = () => {
      let currentNode = product.availableAttributes?.root;
      const path: any = [];

      if (!currentNode) return path;

      while (currentNode && !currentNode.isLeaf) {
        if (currentNode.children) {
          const attributeName = currentNode.attributeName;
          const normalizedAttributeName = normalizeAttributeName(attributeName);
          const displayName = currentNode.displayName;
          const position = currentNode.position;

          path.push({
            attributeName,
            normalizedAttributeName,
            displayName,
            children: currentNode.children,
            position,
          });

          const selectedValue = selectedAttributes[normalizedAttributeName];

          if (selectedValue && currentNode.children) {
            const matchingChildKey = Object.keys(currentNode.children).find(
              (childKey) =>
                normalizeAttributeValue(currentNode.children[childKey].value) ===
                selectedValue
            );

            if (matchingChildKey && currentNode.children[matchingChildKey]) {
              currentNode = currentNode.children[matchingChildKey];
            } else {
              break;
            }
          } else {
            break;
          }
        } else {
          break;
        }
      }

      return path;
    };

    const renderAttributeSelectors = () => {
      const treePath = getCurrentTreePath();

      return treePath.map((level: any, index: any) => {
        if (!level.children) return null;

        const currentSelection =
          selectedAttributes[level.normalizedAttributeName];
        const availableOptions = Object.keys(level.children);

        return (
          <div
            key={`${level.normalizedAttributeName}-${index}`}
            className="space-y-2"
          >
            <label className="font-medium text-gray-700">
              <ToTitleCase text={level.displayName || level.attributeName} />:
            </label>

            <div className="flex flex-wrap gap-2">
              {availableOptions.map((optionKey) => {
                const option = level.children[optionKey];
                const normalizedOptionValue = normalizeAttributeValue(
                  option.value
                );
                const isSelected = currentSelection === normalizedOptionValue;

                return (
                  <button
                    key={optionKey}
                    onClick={() =>
                      handleAttributeChange(level.attributeName, option.value)
                    }
                    className={`px-3 py-2 border rounded-md text-sm font-medium transition-colors ${isSelected
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 cursor-pointer"
                      }`}
                  >
                    <ToTitleCase text={option.value} />
                  </button>
                );
              })}
            </div>
          </div>
        );
      });
    };

    const minOrderQuantity = 25;

    const notifyAddTocart = () => {
      toast.custom(
        (t) => (
          <NotifyAddTocart
            productImage={images[0]}
            qualitySelected={quantity}
            show={t.visible}
            sizeSelected={selectedVariant?.attributes?.capacity || ""}
            variantActive={0}
            price={selectedVariant?.price || 0}
            name={product.name}
            onClose={() => toast.dismiss(t.id)}
          />
        ),
        { position: "top-right", id: "nc-product-notify", duration: 3000 }
      );
    };

    const handleAddToCart = async () => {
      if (!selectedVariant) {
        toast.error("Please select a valid variant");
        return;
      }

      setAddingToCart(true);

      const cartItem = {
        id: product.id,
        productId: product.id,
        variantId: selectedVariant.id,
        quantity,
        price: selectedVariant.price || 0,
        name: product.name,
        images: images,
        identification: product.identification,
        variants: [selectedVariant],
        attributes: product.attributes,
        itemType: ITEM_TYPES.PRODUCTS,
      };

      try {
        const result = await dispatch(addToCart(cartItem));
        if (addToCart.fulfilled.match(result)) {
          notifyAddTocart();
        } else if (addToCart.rejected.match(result)) {
          toast.error(result.payload || "Failed to add to collection");
        }
      } catch (err) {
        toast.error("Something went wrong");
      } finally {
        setAddingToCart(false);
      }
    };

    const openDialogue = () => {
      if (!userId) {
        dispatch(openModal());
        localStorage.setItem("bulkEnquiryIntent", JSON.stringify({
          products: [{
            id: product.id,
            productId: product.productId,
            variantId: selectedVariant?.id,
            qty: quantity,
            price: selectedVariant?.price || 0,
            name: product.name,
            image: images[0] || "",
            images: product.images,
            category: product.categoryName,
            brand: product.brandName,
            description: product.description,
            variants: [selectedVariant],
            itemType: ITEM_TYPES.PRODUCTS
          }],

          quantities: { [`${product.id}-${selectedVariant?.id}`]: quantity }
        }));
        return;
      }
      setIsDialogueOpen(true);
    };
    const closeDialogue = () => setIsDialogueOpen(false);

    const averageRating =
      product.reviews && product.reviews.length > 0
        ? product.reviews.reduce(
          (sum: number, r: any) => sum + Number(r.rating || 0),
          0
        ) / product.reviews.length
        : 0;

    return (
      <div className="space-y-4 p-2">
        <div>
          <h1
            className="text-xl lg:text-3xl font-bold text-gray-900"
            style={{
              wordWrap: "break-word",
              overflowWrap: "break-word",
              wordBreak: "break-all",
            }}
          >
            <ToTitleCase text={product.name} />
          </h1>

          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              {averageRating > 0 && (
                <Rating rating={averageRating || 0} size="sm" />
              )}
              {averageRating > 0 && (
                <span className="font-semibold">{averageRating.toFixed(1)}</span>
              )}
            </div>
            {(() => {
              const analytics = product.analytics || [];
              const trendingEntry = analytics.find(
                (a: any) => a.trendingScore != null
              );
              const score = trendingEntry?.trendingScore ?? 0;

              if (score >= 0.8) {
                return (
                  <GlowingBadge
                    icon={TrendingUp}
                    text="Trending"
                    bgColor="bg-green-500"
                    textColor="text-white"
                    className="ml-2 py-2 border-green-500 bg-green-300 text-green-700"
                    showGlow
                  />
                );
              } else if (score >= 0.7) {
                return (
                  <GlowingBadge
                    icon={Heart}
                    text="Buying Often"
                    bgColor="bg-yellow-500"
                    textColor="text-white"
                    className="ml-2 py-2 border-rose-500 bg-rose-200 text-rose-800"
                    showGlow
                  />
                );
              } else if (score >= 0.5) {
                return (
                  <GlowingBadge
                    icon={Sparkles}
                    text="Curated Choice"
                    bgColor="bg-blue-500"
                    textColor="text-white"
                    className="ml-2 py-2 bg-cyan-200 text-cyan-800"
                    showGlow
                  />
                );
              }
            })()}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 text-sm mt-3">
          <div className="flex items-center min-w-fit">
            <span className="text-gray-600 font-semibold whitespace-nowrap">Brand:</span>
            <span className="ml-2 font-medium break-words">
              <ToTitleCase
                text={product.brand?.name || product.brandName || "N/A"}
              />
            </span>
          </div>
          <div className="flex items-center min-w-fit">
            <span className="text-gray-600 font-semibold whitespace-nowrap">Category:</span>
            <span className="ml-2 font-medium break-words">
              <ToTitleCase
                text={
                  product.category?.name ||
                  product.categoryName ||
                  product.aiMetadata?.modelOutputs?.category ||
                  "N/A"
                }
              />
            </span>
          </div>
          <div className="flex items-center min-w-fit">
            <span className="text-gray-600 font-semibold whitespace-nowrap">Sub Category:</span>
            <span className="ml-2 font-medium break-words">
              <ToTitleCase
                text={
                  product.subCategory?.name ||
                  product.subCategoryName ||
                  "N/A"
                }
              />
            </span>
          </div>
        </div>

        <div className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          ₹{selectedVariant?.price || "0.00"}
          {selectedVariant?.comparePrice && (
            <span>
              <span className="text-lg text-gray-500 line-through ml-2">
                {selectedVariant.comparePrice > selectedVariant.price
                  ? `₹${selectedVariant.comparePrice}`
                  : ""}
              </span>

              {(() => {
                const price = parseFloat(selectedVariant?.price);
                const compare = parseFloat(selectedVariant?.comparePrice);

                if (
                  isNaN(price) ||
                  isNaN(compare) ||
                  compare <= 0 ||
                  price >= compare
                ) {
                  return null;
                }

                const discount = ((compare - price) / compare) * 100;
                const discountText = `${Math.round(discount)}%`;

                if (discount >= 50) {
                  return (
                    <GlowingBadge
                      icon={HandCoins}
                      text={`Steal Deal (${discountText})`}
                      bgColor="bg-red-600"
                      textColor="text-white"
                      className="ml-2"
                    />
                  );
                } else if (discount >= 40) {
                  return (
                    <GlowingBadge
                      icon={HandCoins}
                      text={`Hot Offer (${discountText})`}
                      bgColor="bg-orange-500"
                      textColor="text-white"
                      className="ml-2"
                    />
                  );
                } else if (discount >= 20) {
                  return (
                    <GlowingBadge
                      icon={HandCoins}
                      text={`Smart Pick (${discountText})`}
                      bgColor="bg-amber-300"
                      textColor="text-black"
                      className="ml-2"
                    />
                  );
                } else if (discount >= 15) {
                  return (
                    <GlowingBadge
                      icon={HandCoins}
                      text={`Good Value (${discountText})`}
                      bgColor="bg-rose-500"
                      textColor="text-white"
                      className="ml-2"
                    />
                  );
                } else if (discount >= 12) {
                  return (
                    <GlowingBadge
                      icon={HandCoins}
                      text={`Save More (${discountText})`}
                      bgColor="bg-lime-300"
                      textColor="text-black"
                      className="ml-2"
                    />
                  );
                } else if (discount >= 10) {
                  return (
                    <GlowingBadge
                      icon={HandCoins}
                      text={`Decent Deal (${discountText})`}
                      bgColor="bg-emerald-300"
                      textColor="text-black"
                      className="ml-2"
                    />
                  );
                }

                return null;
              })()}
            </span>
          )}
        </div>

        {renderAttributeSelectors()}

        <div className="w-full md:w-40">
          <NcInputNumber
            min={minOrderQuantity}
            disabled={addingToCart}
            defaultValue={quantity}
            onChange={setQuantity}
            onQuantityChanging={setIsQuantityChanging}
            onInputFocusChange={setIsQuantityInputFocused}
            size="lg"
          />
        </div>

        <Alert className="mt-4 border-blue-200 bg-blue-50">
          <AlertDescription className="text-blue-600">
            <p className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 flex-shrink-0 text-blue-500" />
              <span>
                Listed prices are exclusive of{" "}
                <strong className="font-semibold">
                  GST, Branding, & Delivery
                </strong>{" "}
                costs
              </span>
            </p>
            <p className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 flex-shrink-0 text-blue-500" />
              <span>
                Additional <strong className="font-semibold">discount</strong>{" "}
                would be applied varied on the product quantity purchased
              </span>
            </p>
          </AlertDescription>
        </Alert>
        <Alert className="mt-4 border-green-200 bg-green-50">
          <AlertDescription className="text-green-600">
            <p className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <span>
                Add product to the collection to generate catalog or use any tool
              </span>
            </p>
          </AlertDescription>
        </Alert>

        <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
          <div className="flex flex-col w-full gap-3 md:flex-row md:gap-4 md:flex-1">
            <ButtonPrimary
              className="w-full md:w-40 xl:w-1/3 bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              onClick={handleAddToCart}
              loading={addingToCart}
              disabled={
                addingToCart || isQuantityChanging || isQuantityInputFocused
              }
            >
              {!addingToCart && (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  Add To Collection
                </>
              )}
            </ButtonPrimary>

            <ButtonPrimary
              className="w-full md:w-40 xl:w-1/3 bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              onClick={openDialogue}
              disabled={
                addingToCart || isQuantityChanging || isQuantityInputFocused
              }
            >
              <Mails className="w-5 h-5" />
              Place Enquiry
            </ButtonPrimary>
          </div>
        </div>

        <div className="border-t pt-6 relative">
          {isDialogueOpen && (
            <BulkEnquiryDialogue
              onClose={() => {
                setIsDialogueOpen(false);
                dispatch(openEnquiryDialogue({ products: [], quantities: {} }));
              }}
              products={[
                {
                  id: product.id,
                  productId: product.productId,
                  variantId: selectedVariant?.id,
                  qty: quantity,
                  price: selectedVariant?.price || 0,
                  name: product.name,
                  image: images[0] || "",
                  images: product.images,
                  category: product.categoryName,
                  brand: product.brandName,
                  description: product.description,
                  variants: [selectedVariant],
                  itemType: ITEM_TYPES.PRODUCTS
                },

              ]}
            />
          )}
        </div>
      </div>
    );
  };

const CompanyInfo: React.FC<{
  company: { name: string; rating: number; itemsSold: number };
}> = ({ company }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-primary-gradient rounded-lg flex items-center justify-center">
          <User className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{company.name}</h3>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Rating rating={company.rating} size="sm" />
            <span>{company.rating}</span>
            <span>•</span>
            <span>{company.itemsSold} items sold</span>
          </div>
        </div>
      </div>
      <button className="text-blue-600 text-sm hover:underline">
        Seller's profile
      </button>
    </div>
  );
};

const ReviewsSummary: React.FC<{ reviews: any[] }> = ({ reviews }) => {
  const totalReviews = reviews.length;

  const ratings = reviews.map((r) => Number(r.rating) || 0);
  const overallRating =
    totalReviews > 0
      ? ratings.reduce((sum, r) => sum + r, 0) / totalReviews
      : 0;

  const breakdown = ratings.reduce((acc, rating) => {
    const rounded = Math.round(rating); // Round to nearest whole number
    acc[rounded] = (acc[rounded] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);

  const getBarWidth = (count: number) =>
    totalReviews > 0 ? (count / totalReviews) * 100 : 0;

  return (
    <div className="space-y-4">
      {/* Average Rating */}
      <div className="flex items-center gap-2">
        <Rating rating={overallRating} />
        <span className="text-xl font-semibold">
          {overallRating.toFixed(1)} out of 5
        </span>
      </div>

      <p className="text-sm text-gray-600">
        {totalReviews} global rating{totalReviews !== 1 ? "s" : ""}
      </p>

      {/* Breakdown */}
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((stars) => {
          const count = breakdown[stars] || 0;
          return (
            <div key={stars} className="flex items-center gap-3">
              <span className="text-sm w-5">{stars}</span>
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <div className="flex-1 bg-gray-300 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-yellow-400 h-2 transition-all duration-300"
                  style={{ width: `${getBarWidth(count)}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 w-6 text-right">
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const tabContentVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
};

const ProductTabs: React.FC<{ product: any; selectedVariant: any }> = ({
  product,
  selectedVariant,
}) => {
  const [activeTab, setActiveTab] = useState<
    "description" | "reviews" | "company" | "usage"
  >("description");

  const [isOpenModalViewAllReviews, setIsOpenModalViewAllReviews] =
    useState(false);

  const tabs = [
    { id: "description", label: "Description" },
    // { id: "reviews", label: "Reviews" },
    // { id: "company", label: "Company" },
  ];

  const specifications = product.variants?.[0]?.metadata;

  return (
    <div className="border-t pt-8 overflow-hidden">
      {/* Tab buttons with animations */}
      <div className="flex justify-start flex-wrap gap-2 sm:gap-3 mb-6 border-b border-gray-200 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <motion.button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            whileTap={{ scale: 0.95 }}
            className={`relative px-4 py-2 font-medium transition-colors ${activeTab === tab.id
              ? "text-blue-600"
              : "text-gray-600 hover:text-gray-900"
              }`}
          >
            {tab.label}
            {activeTab === tab.id && (
              <motion.div
                layoutId="underline"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-gradient rounded-full"
              />
            )}
          </motion.button>
        ))}
      </div>

      <div className="min-h-[200px]">
        <AnimatePresence mode="wait">
          {activeTab === "description" && (
            <motion.div
              key="description"
              variants={tabContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <p
                className="text-gray-700 leading-relaxed product-description"
                style={{
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-all",
                }}
              >
                {parse(he.decode(product.description))}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 mt-6 text-sm">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">SKU:</span>
                  <span className="font-medium">
                    {selectedVariant?.sku || "N/A"}
                  </span>
                </div>

                {selectedVariant?.weight ? (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Weight:</span>
                    <span className="font-medium">
                      {selectedVariant.weight} kg
                    </span>
                  </div>
                ) : (
                  ""
                )}

                {selectedVariant?.dimensions &&
                  selectedVariant?.dimensions.length > 0 &&
                  selectedVariant?.dimensions.width > 0 &&
                  selectedVariant?.dimensions.height > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Dimensions:</span>
                      <span className="font-medium">
                        {selectedVariant.dimensions.length || 0} ×{" "}
                        {selectedVariant.dimensions.width || 0} ×{" "}
                        {selectedVariant.dimensions.height || 0} cm
                      </span>
                    </div>
                  )}
                {selectedVariant?.attributes &&
                  Object.entries(selectedVariant.attributes).map(
                    ([key, value]) => (
                      <div
                        key={key}
                        className="flex justify-between py-2 border-b border-gray-200"
                      >
                        <span className="text-gray-600">
                          <ToTitleCase text={key} />:
                        </span>
                        <span className="font-medium">
                          <ToTitleCase text={String(value)} />
                        </span>
                      </div>
                    )
                  )}
              </div>
            </motion.div>
          )}

          {activeTab === "reviews" && (
            <motion.div
              key="reviews"
              variants={tabContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-8"
            >
              <ReviewsSummary reviews={product.reviews} />
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-gray-900">
                    Recent Reviews
                  </h3>
                  <button
                    className="text-blue-600 text-sm hover:underline"
                    onClick={() => setIsOpenModalViewAllReviews(true)}
                  >
                    View All
                  </button>
                </div>
                {product.reviews.slice(0, 4).map((review: any) => (
                  <div
                    key={review.id}
                    className="border-b border-gray-200 pb-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-blue-600">
                        {review.clientCompany} (
                        <span className="font-normal">{review.clientName}</span>
                        )
                      </span>
                    </div>
                    <Rating rating={review.rating} size="sm" />
                    <p className="text-gray-700 text-sm">{review.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === "company" && (
            <motion.div
              key="company"
              variants={tabContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* <CompanyInfo
                company={{
                  name: "360customizer",
                  rating: 4.2,
                  itemsSold: 1290,
                }}
              /> */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">
                  About the Company
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  360Customizer is a trusted electronics retailer with over 5
                  years of experience in providing quality products. We
                  specialize in mobile devices, computers, and home electronics.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Award className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <div className="font-semibold">Quality Assured</div>
                    <div className="text-sm text-gray-600">
                      All products tested
                    </div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Truck className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <div className="font-semibold">Fast Shipping</div>
                    <div className="text-sm text-gray-600">
                      2-3 days delivery
                    </div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <User className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <div className="font-semibold">Expert Support</div>
                    <div className="text-sm text-gray-600">
                      24/7 customer service
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "usage" && (
            <motion.div
              key="usage"
              variants={tabContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <h3 className="font-semibold text-gray-900">Usage Guide</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-medium text-gray-900">Getting Started</h4>
                  <p className="text-gray-700 text-sm mt-1">
                    Unbox your headphones carefully and charge them fully before
                    first use.
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-medium text-gray-900">Setup Process</h4>
                  <p className="text-gray-700 text-sm mt-1">
                    Pair the headphones with your device via Bluetooth and
                    configure noise cancellation settings.
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-medium text-gray-900">Tips & Tricks</h4>
                  <p className="text-gray-700 text-sm mt-1">
                    Keep your headphones updated and use the provided case for
                    best performance and protection.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ModalViewAllReviews
        show={isOpenModalViewAllReviews}
        onCloseModalViewAllReviews={() => setIsOpenModalViewAllReviews(false)}
        reviews={product.reviews}
      />
    </div>
  );
};

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const plpProduct = useAppSelector((state) =>
    selectPLPProductById(state, id!)
  );
  const navigate = useNavigate();

  const productState = useAppSelector(selectProductDetails);
  const loading = useAppSelector(selectProductDetailsLoading);
  const error = useAppSelector(selectProductDetailsError);
  const product: IProduct = productState.product;
  const [filteredImages, setFilteredImages] = useState<string[]>([]);
  const enquiryState = useAppSelector((state) => state.enquiry);
  const [variantSelected, setVariantSelected] = useState<any>(null);
  const [productSelected, setProductSelected] = useState<any>(null);
  const [isDialogueOpen, setIsDialogueOpen] = useState(false);
  const userId = getUserId();

  const normalize = (val: any) => {
    let normalized = String(val).trim();
    while (
      (normalized.startsWith('"') && normalized.endsWith('"')) ||
      (normalized.startsWith("'") && normalized.endsWith("'"))
    ) {
      normalized = normalized.slice(1, -1);
    }
    normalized = normalized.replace(/\\"/g, '"').replace(/\\'/g, "'");
    return normalized;
  };

  const getFilteredImages = (
    product: IProduct | null,
    selectedVariant: any,
    selectedAttributes?: any
  ): string[] => {
    if (!product) return [];

    let attributesToFilter: Record<string, any> = {};

    if (selectedAttributes && Object.keys(selectedAttributes).length > 0) {
      attributesToFilter = selectedAttributes;
    } else if (selectedVariant?.attributes) {
      Object.entries(selectedVariant.attributes).forEach(([key, value]) => {
        attributesToFilter[key.trim().toLowerCase()] = value;
      });
    }

    if (Object.keys(attributesToFilter).length > 0 && product.attributes) {
      const imageChangingSelections: Record<string, any> = {};

      product.attributes.forEach((attr: any) => {
        if (attr.isImageChanging) {
          const attrKey = attr.name.trim().toLowerCase();
          const matchingKey = Object.keys(attributesToFilter).find(
            (key) => key.trim().toLowerCase() === attrKey
          );

          if (matchingKey && attributesToFilter[matchingKey]) {
            imageChangingSelections[attr.name] =
              attributesToFilter[matchingKey];
          }
        }
      });

      if (Object.keys(imageChangingSelections).length > 0) {
        const filtered = filterImageByAttributes(
          product,
          imageChangingSelections
        );

        if (filtered && filtered.images) {
          const images = filtered.images;
          const finalImages = images
            .map((img: any) => (typeof img === "string" ? img : img?.url || ""))
            .filter(Boolean);

          if (finalImages.length > 0) {
            return finalImages;
          }
        }
      }
    }

    if (product.images && Array.isArray(product.images)) {
      if (product.images[0] && product.images[0].images) {
        const fallbackImages = product.images[0].images
          .map((img: any) => (typeof img === "string" ? img : img?.url || ""))
          .filter(Boolean);

        return fallbackImages;
      }

      const directImages = product.images
        .map((img: any) =>
          typeof img === "string" ? img : img?.url || img?.image || ""
        )
        .filter(Boolean);

      return directImages;
    }

    return [];
  };

  useEffect(() => {
    const userId = getUserId();
    if (userId && !enquiryState.isDialogueOpen) {
      const enquiryIntent = localStorage.getItem("bulkEnquiryIntent");
      if (enquiryIntent) {
        const intent = JSON.parse(enquiryIntent);
        dispatch(
          openEnquiryDialogue({
            products: intent.products,
            quantities: intent.quantities,
          })
        );
        setIsDialogueOpen(true);
      }
    }
  }, [dispatch, enquiryState.isDialogueOpen]);

  useEffect(() => {
    setIsDialogueOpen(enquiryState.isDialogueOpen);
  }, [enquiryState.isDialogueOpen]);

  useEffect(() => {
    if (plpProduct) {
      dispatch(setProduct(plpProduct));
      const firstVariant = plpProduct.variants?.[0] || null;
      setProductSelected(firstVariant);

      if (firstVariant?.attributes) {
        const initialAttributes: { [key: string]: string } = {};
        Object.entries(firstVariant.attributes).forEach(([key, value]) => {
          initialAttributes[key.trim().toLowerCase()] = normalize(value);
        });
        setVariantSelected(initialAttributes);
      }
    } else if (id) {
      dispatch(fetchProductDetailById(id));
    }

    return () => {
      dispatch(clearProduct());
    };
  }, [dispatch, id, plpProduct]);

  useEffect(() => {
    if (product) {
      const images = getFilteredImages(
        product,
        productSelected,
        variantSelected
      );

      setFilteredImages(images);
    }
  }, [product, productSelected, variantSelected]);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center">
        <img src={infiniteLoader} alt="Loading..." className="w-[14%]" />
      </div>
    );
  }

  if (error && !loading) {
    return (
      <ErrorFallback
        message={error}
        onRetry={handleBackNavigation}
        buttonText="Go Back"
      />
    );
  }

  if (!product && !loading) {
    return (
      <EmptyState
        icon={PackageSearch}
        title="No Product Found"
        message="Try adjusting your filters or search again to discover more corporate gifts."
        buttonLabel="Go Back To Products"
        onButtonClick={() => navigate("/products")}
      />
    );
  }



  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
      <Helmet>
        <title>{product?.metadata?.seoTitle || "360CUSTOMIZER"}</title>
        <meta
          name="description"
          content={
            product?.metadata?.seoDescription ||
            "Custom products with 360Customizer"
          }
        />
        <meta
          name="keywords"
          content={
            product?.metadata?.tags?.join(", ") ||
            "custom, t-shirt, 360, product"
          }
        />
      </Helmet>
      <div>
        <GoBackBtn className="hidden lg:inline-block absolute -top-11 -left-3 2xl:-top-4" />
        <div className="flex flex-col md:flex-row justify-between gap-10 relative">
          <div className="w-full lg:max-w-lg">
            <ImageGallery
              images={filteredImages}
              product={product}
              attributesSelected={variantSelected}
              itemKey={`${product?.id}-${productSelected?.id}`}
              productSelected={productSelected}
            />
          </div>

          <div className="flex-1">
            <ProductInfo
              product={product}
              images={filteredImages}
              setVariantSelected={setVariantSelected}
              setProductSelected={setProductSelected}
              normalize={normalize}
            />
          </div>
        </div>
      </div>

      <div>
        <div className=" flex flex-col md:flex-row justify-between items-start md:gap-8">
          <div className="w-full relative min-h-[420px]">
            <div className="px-4 sm:px-6 lg:px-0">
              <ProductTabs
                product={product}
                selectedVariant={productSelected}
              />
            </div>
          </div>
        </div>
      </div>

      {
        product?.subCategoryId && (
          <SimilarProducts
            subCategoryId={product.subCategoryId}
            currentProductId={product.id}
          />
        )
      }
    </div >
  );
};
export default ProductDetailPage;
